// Thin same-origin API client. Cookies ride automatically; a 401 anywhere
// flips the app back to the login screen.

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  // A 401 from the login endpoint means "wrong credentials", not "your
  // session expired". Routing it through the unauthorized handler re-rendered
  // the login screen mid-submit, wiping the form and replacing the real
  // reason with "Session ended — sign in again."
  if (res.status === 401 && !path.startsWith('/api/login')) {
    onUnauthorized?.();
    throw new ApiError(401, 'Not signed in.');
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // FastAPI validation errors carry detail as [{loc, msg}, ...] — flatten
      // to something a human can read in a toast.
      if (Array.isArray(body.detail)) {
        detail = body.detail
          .map((d: { loc?: unknown[]; msg?: string }) =>
            `${(d.loc ?? []).slice(1).join('.')}: ${d.msg ?? 'invalid'}`)
          .join('; ') || detail;
      } else {
        detail = body.detail || body.error || detail;
      }
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const get = <T = unknown>(path: string) => api<T>(path);
export const post = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
export const put = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const patch = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
/** DELETE, optionally with a body — which is unusual but is what the
 *  remove-members endpoints take: the resource being deleted is a MEMBERSHIP,
 *  and the ids of what to remove will not fit sensibly in a path. Matches
 *  DELETE /api/stories/{id}/members and /api/incidents/{id}/members. */
export const del = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export interface Me {
  kind: 'user' | 'guest';
  displayName: string;
  isAdmin: boolean;
  isSuperuser?: boolean;
  isOperator?: boolean;
  orgId: string | null;
  userId: string | null;
}

import type { Geometry } from 'geojson';

export interface EntityDto {
  id: string;
  type: string;
  category: string;
  name: string | null;
  attrs: Record<string, unknown>;
  geometry: Geometry | null;
  rev: number;
  validFrom: string;
  changeKind: string;
  ownerOrgId: string;
  ownerUserId: string | null;
  /** The story this version is filed under. null = unfiled, which means
   *  UNIVERSALLY VISIBLE, not hidden — filing narrows, unfiling widens. */
  storyId?: string | null;
  /** WHEN THE THING HAPPENED (migration 031), as opposed to validFrom, which is
   *  when we wrote the row. null means no world clock is known and the client
   *  falls back to validFrom — a fallback that lives in exactly one place,
   *  `when.ts:whenOf()`. Never read these directly; ask when.ts. */
  happenedAt?: string | null;
  /** Width of the estimate in SECONDS. "Overnight" is a real answer six hours
   *  wide, and rendering it as an exact clock time invents precision. */
  happenedSpanS?: number | null;
  /** stated | relative | post | imagery | manual | rule. An inferred time must
   *  not render like a stated one — same principle as the attribution basis. */
  happenedBasis?: string | null;
  /** When we last heard about it. The feed SORTS by this and AGES by
   *  happenedAt; that split is what stops corroboration looking like news. */
  lastReportAt?: string | null;
  /** Which SITUATION this report belongs to (migration 033). null means
   *  UNROUTED — the router has not decided yet — and is the exact OPPOSITE of
   *  storyId's null, which means universal. incidentId carries no visibility at
   *  all, which is what makes a router outage safe rather than a disclosure. */
  incidentId?: string | null;
  /** Audit trail from the identity row: when the object was first created and
   *  by whom (username; null = system-created, e.g. the live pipeline). */
  createdAt?: string;
  createdBy?: string | null;
  edges?: EdgeDto[];
}

export interface EdgeDto {
  id: string;
  type: string;
  label: string;
  direction: 'in' | 'out';
  peerId: string;
  peerName: string | null;
  peerType: string;
  attrs: Record<string, unknown>;
  since: string;
}

export interface RegistryDto {
  categories: { key: string; label: string; domain: string; sort: number }[];
  types: {
    key: string; label: string; category: string;
    geometryKinds: string[]; attrSchema: Record<string, unknown>;
    /** minZoom: the zoom this type's markers first draw at (admin-set,
     *  Unit types tab); unset = the domain default. */
    style: { color?: string; icon?: string; minZoom?: number }; sort: number;
    watchDefault?: boolean;
  }[];
  edgeTypes: { key: string; label: string; directed: boolean }[];
}

/** A shape drawn over a map by hand, private to whoever drew it
 *  (migration 056), on one of their sheets (059). `kind` is how it is DRAWN —
 *  an arrow, a thrust, a freehand stroke and a callout are all LineStrings, a
 *  box, an ellipse and an area all Polygons, a note and a pin Points. */
export interface MarkupDto {
  id: string;
  kind: 'arrow' | 'thrust' | 'stroke' | 'callout' | 'pin' | 'box' | 'ellipse' | 'area' | 'text';
  geometry: Geometry;
  label: string | null;
  color: string;
  width: number;
  dashed: boolean;
  fill: 'none' | 'tint' | 'solid' | 'hatch';
  sheetId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BookmarkDto {
  id: string;
  name: string;
  note: string | null;
  geometry: Geometry;
  createdAt: string;
}

export type StoryStatus = 'active' | 'dormant' | 'archived';
export type StoryVisibility = 'public' | 'org';

/** A tracked situation — a conflict, an investigation, a watch. The list route
 *  returns these FLAT with parentId; the tree is expanded client-side in
 *  story.ts, because viewing a parent unions in its descendants and that must
 *  not cost a request per switch. */
export interface StoryDto {
  id: string;
  slug: string;
  name: string;
  /** Display label only ('conflict', 'investigation', …) — nothing branches on
   *  it, which is why the column carries no CHECK. */
  kind: string;
  /** 'dormant' still shows but stops taking new auto-filed events; 'archived'
   *  is retired and hidden (rows are never deleted — story_id is RESTRICT). */
  status: StoryStatus;
  /** The narrowing axis: 'org' means only ownerOrgId sees the story OR any
   *  entity filed under it. Never inherited from the parent. */
  visibility: StoryVisibility;
  ownerOrgId: string | null;
  parentId: string | null;
  summary: string | null;
  /** MultiPolygon, or null for a story with no geography yet. */
  aoi?: Geometry | null;
  /** [w,s,e,n] of the AOI — what the admin editor edits, since it has no map
   *  handle to draw with. Null whenever aoi is. */
  bbox?: [number, number, number, number] | null;
  /** Filed entities, DIRECT members only. A parent's effective total is the
   *  local subtree walk, the same one subtreeIds() already does. */
  memberCount?: number;
  /** Owning org's slug, alongside ownerOrgId — the admin list shows a name. */
  ownerOrg?: string | null;
  /** MAX(rev) of story_brief; 1 with empty sections means never authored. */
  briefRev?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** A SITUATION — the thing several reports are about (migration 033).
 *
 *  Every field is a PROJECTION of its members, which is why an incident is
 *  scoped to exactly one story: a projection cannot be filtered per-reader. It
 *  carries its own `rev` because /api/updates delivers entities, deletions and
 *  notifications — an incident's title and counts reach no client through its
 *  members' revs. */
export interface IncidentDto {
  id: string;
  rev: number;
  storyId: string | null;
  title: string;
  /** 'auto' | 'model' | 'human'. 'human' FREEZES the title: the router may never
   *  overwrite what a person typed. */
  titleBy: string;
  status: string;
  statusBy?: string;
  mergedIntoId?: string | null;
  memberCount: number;
  /** Distinct source channels across ALL members — the server-side projection,
   *  which sees members the client may not hold. Cards render
   *  max(this, locally counted); pre-035 rows carry 0 until reprojected. */
  channelCount?: number;
  /** Curator-attached URLs (news writeups, videos). Member post links ride
   *  each report's attrs.sources; these are the ones a human added. */
  links?: { url: string; label?: string }[];
  /** 031's encoding, so when.ts needs no second shape. */
  happenedAt: string | null;
  happenedSpanS: number | null;
  lastReportAt: string | null;
  centroid?: { type: string; coordinates: [number, number] } | null;
  /** How many members the location was derived from. 0 with a null centroid is
   *  honest: an incident of region-only reports has no meaningful position. */
  locatedFrom: number;
  locationBasis: string | null;
  /** Only on the detail read. */
  members?: EntityDto[];
}

export interface UpdatesDto {
  rev: number;
  entities: EntityDto[];
  deleted: string[];
  /** Incidents changed above the cursor. Story-gated server-side. */
  incidents?: IncidentDto[];
  /** The batch hit the server's cap, so `rev` is the PAYLOAD's own max rather
   *  than the global one — poll again to collect the remainder. Absent means a
   *  complete batch, i.e. the cursor is caught up. */
  truncated?: boolean;
  notifications: {
    rev: number;
    /** 'alert' (rule firings — belong in the log) | 'suggestion' (Suggestions panel) */
    kind?: string;
    title: string;
    body: string | null;
    entityId: string | null;
    createdAt: string;
  }[];
}
